import { useCallback, useMemo, useState } from "react";
import { Link } from "wouter";
import {
  INDEX_MINT,
  INDEX_SYMBOL,
  STONKFUN_TOKEN,
  shortMint,
} from "../lib/data";
import { ATA_RENT_SOL, fetchPortfolio, fmtToken, isValidPubkey } from "../lib/solana";

function Img({ entry }) {
  if (entry?.imageUrl) {
    return <img src={entry.imageUrl} alt="" loading="lazy" />;
  }
  return (
    <div className="ph" aria-hidden>
      {(entry?.symbol || "?").slice(0, 3)}
    </div>
  );
}

function statusLabel(status) {
  if (status === "funded") return "Open · funded";
  if (status === "empty") return "Open · empty";
  return "Missing ATA";
}

function jupiterBuyUrl(mint) {
  return `https://jup.ag/swap/SOL-${mint}`;
}

function stonkfunUrl(mint) {
  return `https://www.stonkfun.xyz/token/${mint}`;
}

export default function PortfolioPage({ data }) {
  const basket = data?.basket?.entries || [];
  const [wallet, setWallet] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [portfolio, setPortfolio] = useState(null);

  const basketMints = useMemo(() => basket.map((e) => e.mint).filter(Boolean), [basket]);

  const lookup = useCallback(async () => {
    const w = wallet.trim();
    setError(null);
    setPortfolio(null);
    if (!isValidPubkey(w)) {
      setError("Enter a valid Solana wallet address.");
      return;
    }
    setLoading(true);
    try {
      const result = await fetchPortfolio(w, {
        indexMint: data?.index?.mint || INDEX_MINT,
        basketMints,
        basket,
      });
      setPortfolio(result);
    } catch (e) {
      setError(e.message || "Lookup failed");
    } finally {
      setLoading(false);
    }
  }, [wallet, basketMints, basket, data?.index?.mint]);

  const rows = useMemo(() => {
    if (!portfolio) return [];
    const share = (portfolio.sharePct || 0) / 100;
    return basket.map((e) => {
      const held = portfolio.holdings[e.mint]?.ui || 0;
      const ata = portfolio.atas?.find((a) => a.mint === e.mint);
      const w = Number(e.weight || 0);
      return {
        ...e,
        held,
        boardWeight: w,
        cycleShare: share * w,
        ataStatus: ata?.status || "missing",
      };
    });
  }, [portfolio, basket]);

  const missingAtas = useMemo(
    () => (portfolio?.atas || []).filter((a) => a.status === "missing" && a.kind === "basket"),
    [portfolio],
  );

  return (
    <section className="app-section" style={{ paddingTop: "2.5rem" }}>
      <div className="shell">
        <p className="section-kicker">
          <i className="brand-diamond" aria-hidden /> Portfolio
        </p>
        <h2 className="section-title">Track your board</h2>
        <p className="section-lead left">
          {`Paste a wallet to read live $${INDEX_SYMBOL} balance, supply share, basket holdings, and ATA health for the next airdrop.`}
        </p>

        <div className="portfolio-lookup panel">
          <div className="field" style={{ marginBottom: "0.75rem" }}>
            <label htmlFor="pf-wallet">Wallet</label>
            <div className="portfolio-lookup__row">
              <input
                id="pf-wallet"
                placeholder="Solana address"
                value={wallet}
                onChange={(e) => setWallet(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") lookup();
                }}
                autoComplete="off"
                spellCheck={false}
              />
              <button
                type="button"
                className="btn btn-lime"
                onClick={lookup}
                disabled={loading}
              >
                {loading ? "Loading…" : "Look up"}
              </button>
            </div>
          </div>
          {error && <p className="portfolio-error">{error}</p>}
        </div>

        {portfolio && (
          <>
            <div className="portfolio-summary">
              <div className="portfolio-stat">
                <div className="label">{`$${INDEX_SYMBOL}`}</div>
                <strong>{fmtToken(portfolio.index.ui)}</strong>
                <p>Token balance</p>
              </div>
              <div className="portfolio-stat">
                <div className="label">Supply share</div>
                <strong>
                  {portfolio.sharePct == null
                    ? "—"
                    : `${
                        portfolio.sharePct < 0.0001 && portfolio.sharePct > 0
                          ? "<0.0001"
                          : portfolio.sharePct.toFixed(4)
                      }%`}
                </strong>
                <p>{`Of circulating $${INDEX_SYMBOL}`}</p>
              </div>
              <div className="portfolio-stat">
                <div className="label">SOL</div>
                <strong>{portfolio.ataSummary.sol.toFixed(4)}</strong>
                <p>
                  {portfolio.ataSummary.canCoverRent
                    ? `≥ ${ATA_RENT_SOL} rent OK`
                    : `Below ~${ATA_RENT_SOL} ATA rent`}
                </p>
              </div>
              <div className="portfolio-stat">
                <div className="label">ATAs</div>
                <strong>
                  {portfolio.ataSummary.openCount}/{portfolio.atas.length}
                </strong>
                <p>
                  {portfolio.ataSummary.missingCount
                    ? `${portfolio.ataSummary.missingCount} missing`
                    : "All open"}
                </p>
              </div>
            </div>

            {portfolio.index.ui <= 0 && (
              <p className="portfolio-hint">
                {`No $${INDEX_SYMBOL} in this wallet yet. `}
                <a href={STONKFUN_TOKEN} target="_blank" rel="noreferrer">
                  Buy on StonkFun →
                </a>
              </p>
            )}

            <h3 className="portfolio-subhead">ATA troubleshooting</h3>
            <div className="panel ata-panel">
              <ul className="ata-advice">
                {portfolio.ataSummary.advice.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>

              {missingAtas.length > 0 && (
                <div className="ata-selfserve">
                  <h4>Open missing ATAs yourself</h4>
                  <p>
                    You do not have to wait for the worker. Opening an Associated
                    Token Account costs about <strong>~{ATA_RENT_SOL} SOL</strong>{" "}
                    each (reclaimable if you close it later). Easiest path: buy a
                    tiny amount of the missing mint — wallets create the ATA
                    automatically on first receive.
                  </p>
                  <ol className="ata-selfserve__steps">
                    <li>
                      Keep a little SOL in this wallet for rent (~
                      {(ATA_RENT_SOL * Math.max(1, missingAtas.length)).toFixed(3)}{" "}
                      SOL if you open all {missingAtas.length} missing now).
                    </li>
                    <li>
                      For each missing board token below, open{" "}
                      <strong>Jupiter</strong> or <strong>StonkFun</strong> and
                      swap a dust amount of SOL → that token.
                    </li>
                    <li>
                      Or in Phantom / Solflare: use{" "}
                      <strong>Manage token list / Add token</strong>, paste the
                      mint, then receive any amount once.
                    </li>
                    <li>
                      Refresh this page — status should flip to{" "}
                      <em>Open · empty</em> or <em>Open · funded</em>. You are
                      then ready for token airdrops on the next cycle.
                    </li>
                  </ol>
                  <div className="ata-selfserve__links">
                    {missingAtas.map((a) => (
                      <div className="ata-selfserve__chip" key={`miss-${a.mint}`}>
                        <strong>${a.symbol}</strong>
                        <a href={jupiterBuyUrl(a.mint)} target="_blank" rel="noreferrer">
                          Jupiter
                        </a>
                        <a href={stonkfunUrl(a.mint)} target="_blank" rel="noreferrer">
                          StonkFun
                        </a>
                        <a
                          href={`https://solscan.io/token/${a.mint}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Mint
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="ata-table">
                {portfolio.atas.map((a) => (
                  <div className={`ata-row ata-row--${a.status}`} key={a.mint}>
                    <div>
                      <strong>{a.symbol}</strong>
                      <span className="ata-kind">{a.kind}</span>
                    </div>
                    <div>
                      <span className={`ata-badge ata-badge--${a.status}`}>
                        {statusLabel(a.status)}
                      </span>
                    </div>
                    <div className="ata-bal">{fmtToken(a.balance)}</div>
                    <div className="ata-tip">
                      {a.status === "missing" ? (
                        <span className="ata-tip__actions">
                          <a href={jupiterBuyUrl(a.mint)} target="_blank" rel="noreferrer">
                            Open via Jupiter →
                          </a>
                          <a href={stonkfunUrl(a.mint)} target="_blank" rel="noreferrer">
                            StonkFun →
                          </a>
                        </span>
                      ) : a.ata ? (
                        <a
                          href={`https://solscan.io/account/${a.ata}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {shortMint(a.ata, 4)} →
                        </a>
                      ) : (
                        <span>{a.tip}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="portfolio-footnote" style={{ marginTop: "0.85rem" }}>
                If you previously closed a reward ATA the worker opened, it may
                blacklist free recreates and pay that leg in SOL. Opening the ATA
                yourself still lets you receive tokens. Keep accounts open if you
                want board airdrops instead of SOL fallbacks.
              </p>
            </div>

            <h3 className="portfolio-subhead">Current basket vs your wallet</h3>
            <div className="basket-list">
              {rows.map((r) => (
                <article className="basket-row portfolio-row" key={r.mint}>
                  <Img entry={r} />
                  <div className="basket-row__id">
                    <h3>
                      {r.symbol}
                      <em className={`ata-inline ata-inline--${r.ataStatus}`}>
                        {r.ataStatus === "missing" ? " no ATA" : ""}
                      </em>
                    </h3>
                    <p>
                      {r.name}
                      {r.quoteSymbol ? ` · ${r.quoteSymbol}` : ""}
                    </p>
                  </div>
                  <div className="basket-row__weight">
                    <div className="label">Board weight</div>
                    <strong>{(r.boardWeight * 100).toFixed(1)}%</strong>
                    <div className="weight-bar" aria-hidden>
                      <span style={{ width: `${Math.max(6, r.boardWeight * 100)}%` }} />
                    </div>
                  </div>
                  <div className="basket-row__meta">
                    <div>
                      <div className="label">Cycle slice</div>
                      <strong>{(r.cycleShare * 100).toFixed(4)}%</strong>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div className="label">In wallet</div>
                      <strong>{fmtToken(r.held)}</strong>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            <p className="portfolio-footnote">
              Cycle slice = your supply share × board weight. Wallet balances are
              on-chain reads.{" "}
              <Link href="/faq">FAQ</Link>
            </p>
          </>
        )}
      </div>
    </section>
  );
}
